/**
 * The fund page's arithmetic (`components/HoldingsFund.tsx`; the design is
 * `docs/mockups/holdings-fund-page.html`): the treemap's layout, which names a
 * chart draws, which return windows the tiles show. No React, no fetch.
 */
import type { FundHistory, FundRow } from "../api/holdings";

export interface Box { x: number; y: number; w: number; h: number }

/** Squarified treemap: `items` (any `v` > 0) laid into the box, largest first in, each with its rectangle. */
export function squarify<T extends { v: number }>(items: T[], box: Box): (T & { box: Box })[] {
  let { x, y, w, h } = box;
  const total = items.reduce((s, i) => s + i.v, 0);
  if (!total || w <= 0 || h <= 0) return [];
  let rest = items.filter((i) => i.v > 0).map((i) => ({ item: i, a: (i.v * w * h) / total }));
  const out: (T & { box: Box })[] = [];
  while (rest.length) {
    const side = Math.min(w, h);
    const worst = (r: typeof rest): number => {
      const s = r.reduce((t, z) => t + z.a, 0), mx = Math.max(...r.map((z) => z.a)), mn = Math.min(...r.map((z) => z.a));
      return Math.max((side * side * mx) / (s * s), (s * s) / (side * side * mn));
    };
    const row = [rest[0]!];
    let i = 1;
    while (i < rest.length && worst([...row, rest[i]!]) <= worst(row)) { row.push(rest[i]!); i++; }
    const s = row.reduce((t, z) => t + z.a, 0);
    if (w >= h) {
      const cw = s / h; let cy = y;
      for (const r of row) { const ch = r.a / cw; out.push({ ...r.item, box: { x, y: cy, w: cw, h: ch } }); cy += ch; }
      x += cw; w -= cw;
    } else {
      const rh = s / w; let cx = x;
      for (const r of row) { const cw = r.a / rh; out.push({ ...r.item, box: { x: cx, y, w: cw, h: rh } }); cx += cw; }
      y += rh; h -= rh;
    }
    rest = rest.slice(i);
  }
  return out;
}

/** The heatmap's cut-off: positions under this share of the book are not drawn (owner's to set). */
export const TREEMAP_MIN = 0.005;

export interface SectorGroup { sector: string; v: number; rows: FundRow[] }

export type GroupBy = "sector" | "industry";

/** Held positions at or above the cut-off, grouped by sector (or industry), largest group first. */
export function sectorGroups(rows: FundRow[], min = TREEMAP_MIN, by: GroupBy = "sector"): SectorGroup[] {
  const groups = new Map<string, FundRow[]>();
  for (const r of rows) {
    if (r.value <= 0 || (r.pctBook ?? 0) < min) continue;
    const k = r[by] ?? "Other";
    groups.set(k, [...(groups.get(k) ?? []), r]);
  }
  return [...groups.entries()]
    .map(([sector, rs]) => ({ sector, v: rs.reduce((s, r) => s + r.value, 0), rows: rs.sort((a, b) => b.value - a.value) }))
    .sort((a, b) => b.v - a.v);
}

/** Sectors in the order of the newest quarter's share, so the legend and the labels agree. */
export function sectorKeys(h: FundHistory): string[] {
  const periods = Object.keys(h.sectors).sort();
  const last = h.sectors[periods[periods.length - 1] ?? ""] ?? {};
  const all = new Set(periods.flatMap((p) => Object.keys(h.sectors[p] ?? {})));
  return [...all].sort((a, b) => (last[b] ?? 0) - (last[a] ?? 0));
}

export type TopMode = "now" | "each";

/** The names the top-5 chart stacks, and each one's share in a quarter. */
export function topSeries(h: FundHistory, mode: TopMode): { names: string[]; value: (period: string, name: string) => number } {
  const periods = Object.keys(h.top).sort();
  if (mode === "now") {
    const last = h.now[periods[periods.length - 1] ?? ""] ?? {};
    const names = Object.entries(last).sort((a, b) => b[1] - a[1]).map(([n]) => n);
    return { names, value: (p, n) => h.now[p]?.[n] ?? 0 };
  }
  const names = [...new Set(periods.flatMap((p) => Object.entries(h.top[p] ?? {}).sort((a, b) => b[1] - a[1]).map(([n]) => n)))];
  return { names, value: (p, n) => h.top[p]?.[n] ?? 0 };
}

/** Few quarters are drawn as columns: an area over three points draws trends that are not there. */
export const COLUMNS_UP_TO = 8;

export interface ReturnTile { key: string; label: string; value: number | null; spy: number | null; need: string }

/**
 * The four return tiles: YTD, the last 12 months, the last calendar year, 3 years.
 * `value` null means the fund has too little history; `need` says what is missing.
 */
export function returnTiles(h: FundHistory): ReturnTile[] {
  const year = Object.keys(h.returns).filter((k) => /^\d{4}$/.test(k)).sort().pop()
    ?? String(new Date().getFullYear() - 1);
  const tiles: [string, string, string][] = [
    ["YTD", `YTD ${Number(year) + 1}`, "needs last year's Q4"],
    ["12M", "Last 12 months", "needs four quarters"],
    [year, year, `needs Q4 ${String(Number(year) - 1).slice(2)} onward`],
    ["3Y", "3 yr", "needs three years"],
  ];
  return tiles.map(([key, label, need]) => ({
    key, label, need, value: h.returns[key]?.value ?? null, spy: h.returns[key]?.spy ?? null,
  }));
}

/** A position's colour on the heatmap: what the fund did with it this quarter. */
export function actionOf(r: FundRow): "NEW" | "ADD" | "TRIM" | "HOLD" {
  return r.status === "NEW" || r.status === "ADD" || r.status === "TRIM" ? r.status : "HOLD";
}

/**
 * The sector line under the pointer: `t` is the pointer's position in quarters
 * (fractional, 0 = oldest), `v` its height as a share of the book. Lines are
 * straight between quarters, so each is read where the pointer is, not at the
 * nearest quarter. Null when no line is within `tol` of it.
 */
export function nearestSector(h: FundHistory, keys: string[], t: number, v: number, tol: number): string | null {
  const periods = Object.keys(h.sectors).sort();
  if (!periods.length) return null;
  const i = Math.max(0, Math.min(periods.length - 1, Math.floor(t)));
  const j = Math.min(periods.length - 1, i + 1), f = Math.max(0, Math.min(1, t - i));
  let best: string | null = null, gap = tol;
  for (const k of keys) {
    const a = h.sectors[periods[i]!]?.[k] ?? 0, b = h.sectors[periods[j]!]?.[k] ?? 0;
    const d = Math.abs(a + (b - a) * f - v);
    if (d <= gap) { gap = d; best = k; }
  }
  return best;
}
